<script>
import http from "@/helpers/http";
import Alert from "@/helpers/alert";
import { Date as BrDate } from "@/helpers/date";
import Loader from "@/helpers/loader";
import Modal from "@/helpers/modal";

export default {
  name: "ChargeNegotiationModal",
  props: {
    subscriber: { type: Object, default: null },
    placeId: { type: [Number, String], required: true },
  },
  emits: ['negotiationDone'],
  data() {
    return {
      unpaidCharges: [],
      selectedChargeIds: [],
      installmentsCount: 1,
      firstDueDate: null,
      paymentMethod: null,
      proposal: null,
      installments: [],
      isSubmitting: false,
      submitError: null,
    };
  },
  computed: {
    subscriberId() {
      return this.subscriber?.id_subscriber;
    },
    canSubmit() {
      return this.selectedChargeIds.length > 0 && this.proposal && this.installments.length > 0 && this.paymentMethod;
    },
    selectedChargesPayload() {
      const byId = new Map(this.unpaidCharges.map(c => [c.id_charge, c]));
      return this.selectedChargeIds
        .map(id => byId.get(id))
        .filter(Boolean)
        .map(c => ({
          id_charge: c.id_charge,
          due_date: c.charge_due_date,
          charge_amount: c.charge_amount,
        }));
    },
  },
  methods: {
    open() {
      if (!this.subscriberId) {
        Alert.error('Assinante inválido.');
        return;
      }

      this.reset();
      this.paymentMethod = this.subscriber?.plan_payment_method || null;
      this.firstDueDate = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

      Modal.toggle('#chargeNegotiationModal');
      this.loadUnpaid();
    },
    close() {
      if (this.isSubmitting) {
        return;
      }
      Modal.toggle('#chargeNegotiationModal');
      this.reset();
    },
    reset() {
      this.unpaidCharges = [];
      this.selectedChargeIds = [];
      this.installmentsCount = 1;
      this.proposal = null;
      this.installments = [];
      this.isSubmitting = false;
      this.submitError = null;
    },
    async loadUnpaid() {
      Loader.show('Carregando parcelas...', 'Aguarde');
      try {
        const response = await http.get(`/places/${this.placeId}/subscribers/${this.subscriberId}/charges/unpaid`);
        this.unpaidCharges = response.data || [];
      } catch (e) {
        Alert.error(e.response?.data?.message || 'Erro ao carregar parcelas em aberto.');
      } finally {
        Loader.close();
      }
    },
    toggleAll(checked) {
      if (checked) {
        this.selectedChargeIds = this.unpaidCharges.map(c => c.id_charge);
      } else {
        this.selectedChargeIds = [];
      }
      this.proposal = null;
      this.installments = [];
    },
    async generateProposal() {
      if (this.selectedChargeIds.length === 0) {
        Alert.info('Selecione ao menos uma parcela.');
        return;
      }

      this.submitError = null;
      Loader.show('Gerando proposta...', 'Aguarde');
      try {
        const response = await http.post(
          `/places/${this.placeId}/subscribers/${this.subscriberId}/charges/negotiations/proposal`,
          {
            charges: this.selectedChargesPayload,
            installments: this.installmentsCount,
            first_due_date: this.firstDueDate,
          }
        );

        this.proposal = response.data;
        //this.paymentMethod = this.proposal?.plan_payment_method || this.paymentMethod;
        this.buildInstallmentsSchedule();
      } catch (e) {
        Alert.error(e.response?.data?.message || e.response?.data?.error || 'Erro ao gerar proposta.');
      } finally {
        Loader.close();
      }
    },
    buildInstallmentsSchedule() {
      if (!this.proposal) return;
      const count = Math.max(1, Number(this.installmentsCount || 1));
      const base = Number(this.proposal.installment_amount || 0);

      const first = this.firstDueDate ? new Date(this.firstDueDate + 'T00:00:00') : new Date();

      const list = [];
      let sum = 0;
      for (let i = 0; i < count; i++) {
        const due = new Date(first);
        due.setMonth(due.getMonth() + i);

        let amount = base;
        if (i === count - 1) {
          const total = Number(this.proposal.negotiated_amount || 0);
          const diff = Number((total - (sum + amount)).toFixed(2));
          amount = Number((amount + diff).toFixed(2));
        }

        sum = Number((sum + amount).toFixed(2));

        list.push({
          due_date: due.toISOString().slice(0, 10),
          installment_amount: amount,
        });
      }

      this.installments = list;
    },
    async effectuate() {
      if (!this.canSubmit) {
        Alert.info('Preencha a proposta e as parcelas da negociação.');
        return;
      }

      const result = await Swal.fire({
        title: 'Efetivar negociação',
        text: 'A operação de negociação não poderá ser estornada. Deseja continuar?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, efetivar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc3545',
      });

      if (!result?.isConfirmed) {
        return;
      }

      this.isSubmitting = true;
      this.submitError = null;
      try {
        const payload = {
          nominal_amount: this.proposal.nominal_amount,
          penalty_amount: this.proposal.penalty_amount,
          interest_amount: this.proposal.interest_amount,
          negotiated_amount: this.proposal.negotiated_amount,
          installments: this.installmentsCount,
          installment_amount: this.proposal.installment_amount,
          first_due_date: this.firstDueDate,
          payment_method: this.paymentMethod,
          origin_charge_ids: this.selectedChargeIds,
          installments_detail: this.installments,
        };

        await http.post(
          `/places/${this.placeId}/subscribers/${this.subscriberId}/charges/negotiations/effectuate`,
          payload
        );

        Alert.success('Negociação realizada com sucesso.');
        this.isSubmitting = false;
        this.$emit('negotiationDone');
        this.close();
      } catch (e) {
        const message = e.response?.data?.message || e.response?.data?.error || e.response.data || 'Erro ao efetivar negociação.';
        this.submitError = message;
        Alert.error(message);
      } finally {
        this.isSubmitting = false;
      }
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2).replace('.', ',');
    },
    formatDate(date) {
      return BrDate.formatLocalDate(date);
    },
    statusBadgeClass(charge) {
      return charge?.is_overdue ? 'badge bg-danger' : 'badge bg-success';
    },
  }
}
</script>

<template>
  <div class="modal fade" id="chargeNegotiationModal" tabindex="-1" data-bs-keyboard="false"
       data-bs-backdrop="static" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Negociação de débitos</h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>

        <div class="modal-body">
          <div v-if="submitError" class="alert alert-danger">
            {{ submitError }}
          </div>

          <div class="mb-4">
            <div class="fw-bold mb-2">Parcelas em aberto</div>

            <div v-if="unpaidCharges.length === 0" class="text-muted">
              Nenhuma parcela disponível para negociação.
            </div>

            <div v-else class="table-responsive">
              <table class="table table-striped align-middle">
                <thead>
                <tr>
                  <th style="width: 40px;">
                    <input type="checkbox" class="form-check-input"
                           :checked="selectedChargeIds.length === unpaidCharges.length"
                           @change="toggleAll($event.target.checked)"/>
                  </th>
                  <th>#</th>
                  <th>Referência</th>
                  <th>Vencimento</th>
                  <th>Valor</th>
                  <th>Situação</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(charge, idx) in unpaidCharges" :key="charge.id_charge">
                  <td>
                    <input type="checkbox"
                           class="form-check-input"
                           :value="charge.id_charge"
                           v-model="selectedChargeIds"
                           @change="proposal = null; installments = []"/>
                  </td>
                  <td>{{ idx + 1 }}</td>
                  <td>{{ charge.charge_reference || '-' }}</td>
                  <td>{{ formatDate(charge.charge_due_date) }}</td>
                  <td>R$ {{ formatMoney(charge.charge_amount) }}</td>
                  <td>
                    <span :class="statusBadgeClass(charge)">
                      {{ charge.is_overdue ? 'Vencida' : 'No prazo' }}
                    </span>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="row g-3 mb-4">
            <div class="col-md-3">
              <label class="form-label">Qtd. parcelas</label>
              <input type="number" class="form-control" min="1" v-model.number="installmentsCount"
                     @change="proposal=null; installments=[]"/>
            </div>
            <div class="col-md-3">
              <label class="form-label">1º vencimento</label>
              <input type="date" class="form-control" v-model="firstDueDate"
                     @change="proposal=null; installments=[]"/>
            </div>
            <div class="col-md-3">
              <label class="form-label">Método de pagamento</label>
              <select class="form-control" v-model="paymentMethod">
                <option value="pix">PIX</option>
                <option value="bank_slip">Boleto</option>
                <option value="money">Dinheiro</option>
                <option value="credit_card">Cartão</option>
              </select>
            </div>
            <div class="col-md-3 d-flex align-items-end">
              <button class="btn btn-primary w-100" type="button" @click="generateProposal">
                Gerar proposta
              </button>
            </div>
          </div>

          <div v-if="proposal" class="mb-4">
            <div class="fw-bold mb-2">Resumo da proposta</div>
            <div class="row g-3">
              <div class="col-md-3">
                <div class="text-muted">Nominal</div>
                <div class="fw-bold">R$ {{ formatMoney(proposal.nominal_amount) }}</div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Multa</div>
                <div class="fw-bold">R$ {{ formatMoney(proposal.penalty_amount) }}</div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Juros</div>
                <div class="fw-bold">R$ {{ formatMoney(proposal.interest_amount) }}</div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Total negociado</div>
                <div class="fw-bold">R$ {{ formatMoney(proposal.negotiated_amount) }}</div>
              </div>
            </div>
          </div>

          <div v-if="installments.length" class="mb-2">
            <div class="fw-bold mb-2">Parcelas destino</div>
            <div class="table-responsive">
              <table class="table table-bordered align-middle">
                <thead>
                <tr>
                  <th style="width: 70px;">#</th>
                  <th>Vencimento</th>
                  <th style="width: 220px;">Valor</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(inst, idx) in installments" :key="idx">
                  <td>{{ idx + 1 }}</td>
                  <td>
                    <input type="date" class="form-control" v-model="inst.due_date"/>
                  </td>
                  <td>
                    <input type="number" step="0.01" class="form-control" v-model.number="inst.installment_amount"/>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
            <div class="text-muted small">
              Dica: você pode ajustar datas/valores antes de efetivar.
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-light" :disabled="isSubmitting" @click="close">Cancelar</button>
          <button type="button" class="btn btn-success" :disabled="!canSubmit || isSubmitting" @click="effectuate">
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {{ isSubmitting ? 'Efetivando...' : 'Efetivar negociação' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

